import { Fragment } from "react";

import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import copy from "copy-to-clipboard";

import { awsRealTimeTxt } from "../../../../../assets/texts/Settings/aws";
import { type CodeSnippet } from "../../types";
import { isBucketNameValid } from "../../utils";

type CommandLineDialogProps = {
  open: boolean;
  setOpen: (boolean) => void;
  cliCommands: string[];
  onCopy: () => void;
  codeSnippets: CodeSnippet[];
  bucketName: string;
  onBucketNameChange: (name: string) => void;
  showBucketNameInput: boolean;
};

/**
 * Show CLI commands for stack creation
 * @param open
 * @param setOpen
 * @param cliCommands
 * @param onCopy - callback that will be called once use copied the commands
 * @param codeSnippets
 * @param bucketName
 * @param onBucketNameChange
 * @param showBucketNameInput
 * @constructor
 */
export const CommandLineDialog = ({
  open,
  setOpen,
  cliCommands,
  onCopy,
  codeSnippets,
  bucketName,
  onBucketNameChange,
  showBucketNameInput,
}: CommandLineDialogProps) => {
  const bucketNameIsValid = isBucketNameValid(bucketName);

  const handleBucketNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onBucketNameChange(e.target.value);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      maxWidth="md"
    >
      <DialogTitle>{awsRealTimeTxt.AWS_CLOUD_SHELL_INSTRUCTIONS_TITLE}</DialogTitle>
      <DialogContent>
        <Alert severity="info">{awsRealTimeTxt.AWS_CLOUD_SHELL_INSTRUCTIONS}</Alert>
      </DialogContent>
      <DialogContent>
        {showBucketNameInput && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <TextField
              value={bucketName}
              onChange={handleBucketNameChange}
              fullWidth
              variant="outlined"
              label={awsRealTimeTxt.S3_BUCKET_NAME_LABEL}
              placeholder={awsRealTimeTxt.S3_BUCKET_NAME_PLACEHOLDER}
              required
              helperText={awsRealTimeTxt.REAL_TIME_DATA_BUCKET_NAME_ERROR_TXT}
              error={!bucketNameIsValid}
            />
          </Box>
        )}
        {cliCommands.map((c, index) => (
          <Box
            key={index}
            sx={{
              bgcolor: "background.default",
              p: 1,
              display: "flex",
            }}
          >
            <Box
              sx={{
                pr: 1,
              }}
            >
              <IconButton
                onClick={() => {
                  copy(c);
                  onCopy();
                }}
                size="large"
              >
                <FileCopyOutlinedIcon />
              </IconButton>
            </Box>
            <Typography sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }} variant="body1">
              {c}
            </Typography>
          </Box>
        ))}
        {codeSnippets.map((c, index) => (
          <Fragment key={index}>
            <Divider />
            <Typography
              variant="body2"
              sx={{
                fontStyle: "italic",
                p: 1,
              }}
            >
              {c.name}
            </Typography>
            <Box
              sx={{
                bgcolor: "background.default",
                p: 1,
                display: "flex",
              }}
            >
              <Box
                sx={{
                  pr: 1,
                }}
              >
                <IconButton
                  onClick={() => {
                    copy(c.code);
                    onCopy();
                  }}
                  size="large"
                >
                  <FileCopyOutlinedIcon />
                </IconButton>
              </Box>
              <Typography sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }} variant="body1">
                <DialogContent key={index}>
                  <Typography variant="body2">
                    <code>{c.code}</code>
                  </Typography>
                </DialogContent>
              </Typography>
            </Box>
          </Fragment>
        ))}
      </DialogContent>

      <Divider />
      <DialogActions>
        <Button
          onClick={() => {
            setOpen(false);
          }}
          color="primary"
          variant="contained"
        >
          {awsRealTimeTxt.CANCEL_BTN}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
